import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { PastVisitsFiltersSchema, PastVisitsFiltersValues } from 'schemas/PastVisitsFilters'
import FormControl from '@ui-components-3/ui/lib/components/FormControl'
import Input from '@ui-components-3/ui/lib/components/Input'
import DateTimePicker from '@ui-components-3/ui/lib/components/DateTimePicker'
import Menu from '@ui-components-3/ui/lib/components/Menu'
import BarsFilterIcon from '@ui-components-3/icons/lib/solid/BarsFilter'
import useId from '@ui-components-3/ui/lib/hooks/useId'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import User from 'singletons/User'

type RenderFormProps = {
  id: string
  onSubmit: (values: PastVisitsFiltersValues) => void
  defaultValues?: Partial<PastVisitsFiltersValues>
}

const RenderForm = ({ id, onSubmit, defaultValues }: RenderFormProps) => {
  const { isMemberIdFilterFlagEnabled } = User
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, touchedFields },
    setError,
    reset,
  } = useForm({
    resolver: yupResolver(PastVisitsFiltersSchema),
    mode: 'onSubmit',
    defaultValues,
  })

  const getFieldError = useCallback(
    (name: string) => {
      return !!touchedFields?.[name] && typeof errors?.[name]?.message === 'string' ? errors[name].message : undefined
    },
    [errors, touchedFields],
  )

  const memberIdError = getFieldError('memberId')
  const pendingVisitIdError = getFieldError('pendingVisitId')
  const dateError = getFieldError('date')

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  return (
    <form className="flex flex-col gap-5 p-4 bg-white" onSubmit={handleSubmit(onSubmit)} id={id}>
      {!!isMemberIdFilterFlagEnabled && (
        <FormControl label="Member ID" helperVariant="error" helper={memberIdError}>
          <Input
            autoComplete="off"
            placeholder="Member ID"
            variant={memberIdError ? 'error' : undefined}
            {...register('memberId')}
          />
        </FormControl>
      )}
      <FormControl label="Visit ID" helperVariant="error" helper={pendingVisitIdError}>
        <Input
          autoComplete="off"
          placeholder="Visit ID"
          variant={pendingVisitIdError ? 'error' : undefined}
          {...register('pendingVisitId')}
        />
      </FormControl>
      <Controller
        name={'date'}
        control={control}
        render={({ field }) => (
          <FormControl label="Date" helperVariant="error" helper={dateError}>
            <DateTimePicker
              {...field}
              onChange={(v: string | Date) => {
                field.onChange(v ? moment(v).format('MM-DD-YYYY') : null)
                field?.onBlur()
              }}
              onError={(e) => {
                setError('date', e ? { type: 'custom', message: e } : null)
                field.onChange(e ? 'Invalid date' : null)
              }}
              autoComplete="off"
              format="MM-dd-yyyy"
              variant={dateError ? 'error' : undefined}
            />
          </FormControl>
        )}
      />
    </form>
  )
}

type PastVisitsFiltersFormProps = {
  currentFilters: Partial<PastVisitsFiltersValues>
  onApply: (filters: PastVisitsFiltersValues) => void
  onClear: () => void
}

const PastVisitsFiltersForm = ({ currentFilters, onApply, onClear }: PastVisitsFiltersFormProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const formId = useId('filters')

  const onSubmit = useCallback(
    (values) => {
      onApply?.(values)
      setIsOpen(false)
    },
    [onApply],
  )

  const onClearClick = useCallback(() => {
    onClear?.()
    setIsOpen(false)
  }, [onClear])

  const currentFiltersCount = useMemo(
    () => Object.values(currentFilters || {}).filter(Boolean).length,
    [currentFilters],
  )

  const defaultValues = useMemo(() => PastVisitsFiltersSchema.cast(currentFilters), [currentFilters])

  return (
    <Menu
      className={classNames('btn self-end gap-3', currentFiltersCount ? 'btn-neutral' : 'btn-neutral-outlined')}
      type="button"
      portal
      items={[]}
      opened={isOpen}
      onOpenChange={setIsOpen}
      prefix={<RenderForm id={formId} onSubmit={onSubmit} defaultValues={defaultValues} />}
      suffix={
        <div className="pt-2 px-4 pb-4 flex flex-col gap-4 bg-white">
          <hr />
          <div className="flex justify-between items-center gap-4">
            <button type="button" className="btn btn-primary-borderless flex-1" onClick={onClearClick}>
              Clear All
            </button>
            <button type="submit" form={formId} className="btn btn-primary flex-1">
              Apply
            </button>
          </div>
        </div>
      }
    >
      <BarsFilterIcon aria-hidden="true" />
      Filters {currentFiltersCount ? `(${currentFiltersCount})` : ''}
    </Menu>
  )
}

export default PastVisitsFiltersForm
